import { useMutation } from '@tanstack/react-query';
import { type AxiosResponse, isAxiosError } from 'axios';
import { Card } from 'primereact/card';
import ElevatedHelpButton from '@/components/buttons/ElevatedHelpButton';
import Container, { Column } from '@/components/container/Container';
import { TextBodyMedium, TextHeadlineExtraLarge, TextLabelLarge2 } from '@/components/text/Texts';
import { showError, showSuccess } from '@/modules/notification/engine/notification.library';
import { useSignupStore } from '@/store';
import type { RequestActivationEmailErrorResponse, RequestActivationEmailSuccessResponse, SignupState } from '@/types';
import { resendActivationEmail } from '../../engine/authentication.mutations';
import Footer from './Footer';
import '../../authentication-screens.styles.css';
import { Button } from 'primereact/button';
import SignUpStepper from './SignUpStepper';

const CheckEmailScreen = () => {
	const { user } = useSignupStore((state: SignupState) => state);

	const { isPending, isSuccess, mutate } = useMutation({
		mutationFn: () => resendActivationEmail(user.email),
		onSuccess: async ({ data }: AxiosResponse<RequestActivationEmailSuccessResponse>) => {
			showSuccess('Activation email resent successfully.');
		},
		onError: (error) => {
			if (!isAxiosError<RequestActivationEmailErrorResponse>(error)) return;
			showError(error.message ?? '', { summary: 'Error' });
		},
	});

	return (
		<Container className="w-full h-full bg-primary100 flex justify-center">
			<Column className="gap-2 h-full justify-center relative tablet:w-full tablet:px-8">
				<Card unstyled pt={{ content: { className: 'flex flex-row rounded-[28px] bg-white tablet:w-full tablet:flex-col' } }}>
					<Column className="w-[585px] px-[56px] py-[52px] tablet:w-full tablet:px-8 tablet:py-8">
						<TextHeadlineExtraLarge className="mb-6 text-black" text="Check your email" />
						<TextBodyMedium
							className="mb-6 text-black"
							text={`To activate your account, kindly follow the instructions we've just sent to ${user.email}. If you encounter any difficulty locating the email, please ensure that the email address you provided is accurate and also check your spam folder.`}
						/>
						<TextLabelLarge2 className="mb-3 text-black" text="Email" />
						<TextBodyMedium className="mb-10 text-black" text={user.email} />
						<Button
							label={isPending ? "Resending email..." : "Resend Email"}
							loading={isPending}
							disabled={isPending}
							onClick={() => mutate()}
							pt={{
								root: { className: 'gap-2 px-3 py-2 bg-transparent self-start' },
								label: { className: 'font-poppins text-label-large2 text-accent60' },
							}}
						/>
					</Column>
					<SignUpStepper currentStep={1} />
				</Card>
				<Footer />
				<ElevatedHelpButton className="absolute right-0 bottom-[70px] tablet:right-8" />
			</Column>
		</Container>
	);
};

export default CheckEmailScreen;
